import { lambdaClient } from '../config/http.client';

// Re-export client for backward compatibility
const apiClient = lambdaClient;

/**
 * Get models configuration from database
 * @returns {Promise<Object>} Models configuration with default model
 */
export const getModelsConfig = async () => {
  try {
    const response = await apiClient.get('/models/config');
    return response.data;
  } catch (error) {
    console.error('Failed to fetch models config:', error);
    throw error;
  }
};

/**
 * Generate code based on requirements
 * Returns job_id immediately for async processing
 * @param {Object} params - Generation parameters
 * @param {string} params.requirements - Requirements text
 * @param {string} params.language - Programming language
 * @param {string} params.framework - Framework (optional)
 * @param {Object} params.layers - Layer selection flags
 * @returns {Promise<Object>} {success: true, job_id: string, status: 'queued'}
 */
export const generateCode = async (params) => {
  try {
    const response = await apiClient.post('/code/generate', params);
    return response.data; // Returns {success: true, job_id: 'xxx', status: 'queued'}
  } catch (error) {
    console.error('Code generation failed:', error);
    throw error;
  }
};

/**
 * Generate a title for code based on requirements
 * @param {string} requirements - Requirements text
 * @returns {Promise<Object>} {success: true, title: string}
 */
export const generateTitle = async (requirements) => {
  try {
    const response = await apiClient.post('/title/generate', {
      input_text: requirements,
      max_length: 100
    });
    return response.data;
  } catch (error) {
    console.error('Failed to generate title:', error);
    throw error;
  }
};

/**
 * Get job status and results
 * @param {string} jobId - Job ID returned from generateCode
 * @returns {Promise<Object>} Job status and results
 */
export const getJobStatus = async (jobId) => {
  try {
    const response = await apiClient.get(`/agentcore/jobs/${jobId}`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch job status:', error);
    throw error;
  }
};

/**
 * Get all code generation jobs
 * @returns {Promise<Object>} {success: true, jobs: Array}
 */
export const getAllJobs = async () => {
  try {
    const response = await apiClient.get('/agentcore/jobs?type=code_generation');
    return response.data;
  } catch (error) {
    console.error('Failed to fetch jobs:', error);
    throw error;
  }
};

/**
 * Delete a code generation job
 * @param {string} jobId - Job ID to delete
 * @returns {Promise<Object>} {success: true}
 */
export const deleteJob = async (jobId) => {
  try {
    const response = await apiClient.delete(`/agentcore/jobs/${jobId}`);
    return response.data;
  } catch (error) {
    console.error('Failed to delete job:', error);
    throw error;
  }
};

/**
 * Save generated code to database (history)
 * @param {Object} codeData - Generated code data to save
 * @returns {Promise<Object>} {success: true, code_id: string}
 */
export const saveCode = async (codeData) => {
  try {
    const response = await apiClient.post('/code/save', codeData);
    return response.data;
  } catch (error) {
    console.error('Failed to save code:', error);
    throw error;
  }
};

/**
 * Get code generation history
 * @param {number} limit - Number of records to fetch
 * @returns {Promise<Object>} {success: true, codes: Array}
 */
export const getCodeHistory = async (limit = 20) => {
  try {
    const response = await apiClient.get(`/code?limit=${limit}`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch code history:', error);
    throw error;
  }
};

/**
 * Get a specific code generation by ID
 * @param {string} codeId - Code ID
 * @returns {Promise<Object>} Code data
 */
export const getCodeById = async (codeId) => {
  try {
    const response = await apiClient.get(`/code/${codeId}`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch code:', error);
    throw error;
  }
};

/**
 * Delete a code generation from history
 * @param {string} codeId - Code ID to delete
 * @returns {Promise<Object>} {success: true}
 */
export const deleteCode = async (codeId) => {
  try {
    const response = await apiClient.delete(`/code/${codeId}`);
    return response.data;
  } catch (error) {
    console.error('Failed to delete code:', error);
    throw error;
  }
};

/**
 * Toggle favorite status for a code generation
 * @param {string} codeId - Code ID
 * @param {boolean} isFavorite - New favorite status
 * @returns {Promise<Object>} {success: true}
 */
export const toggleFavorite = async (codeId, isFavorite) => {
  try {
    const response = await apiClient.put(`/code/${codeId}/favorite`, {
      is_favorite: isFavorite
    });
    return response.data;
  } catch (error) {
    console.error('Failed to toggle favorite:', error);
    throw error;
  }
};

/**
 * Get favorite code generations
 * @param {number} limit - Number of records to fetch
 * @returns {Promise<Object>} {success: true, codes: Array}
 */
export const getFavoriteCodes = async (limit = 20) => {
  try {
    const response = await apiClient.get(`/code?is_favorite=true&limit=${limit}`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch favorites:', error);
    throw error;
  }
};

/**
 * Download generated files as ZIP
 * @param {Array} files - Array of file objects with name and content
 * @param {string} projectName - Name for the ZIP file
 */
export const downloadFilesAsZip = async (files, projectName = 'generated-code') => {
  try {
    // Use JSZip library to create zip
    const JSZip = (await import('jszip')).default;
    const zip = new JSZip();
    
    // Add each file to the zip
    files.forEach(file => {
      zip.file(file.path || file.name, file.content);
    });
    
    // Generate zip file
    const content = await zip.generateAsync({ type: 'blob' });
    
    // Create download link
    const url = window.URL.createObjectURL(content);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${projectName}.zip`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    return true;
  } catch (error) {
    console.error('Failed to create ZIP:', error);
    throw error;
  }
};

/**
 * Download a single file
 * @param {Object} file - File object with name and content
 */
export const downloadSingleFile = (file) => {
  const blob = new Blob([file.content], { type: 'text/plain' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = file.name;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const codegenService = {
  generateCode,
  generateTitle,
  getJobStatus,
  getAllJobs,
  deleteJob,
  saveCode,
  getCodeHistory,
  getCodeById,
  deleteCode,
  toggleFavorite,
  getFavoriteCodes,
  downloadFilesAsZip,
  downloadSingleFile
};

export default codegenService;
